import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { api, PulseClient, PulseRelationshipsData, PulseMarketData, Commitment } from '../lib/api';
import { AppOutletContext } from '../App';
import { EmptyState } from '../components/EmptyState';
import { MoodTimeline } from '../components/MoodTimeline';

// ── Sentiment dot color mapping (mirrors Dashboard.tsx logic) ──────────────
const SENTIMENT_CAP8_COLOR: Record<string, string> = {
  very_positive: '#22c55e',
  positive:      '#86efac',
  neutral:       '#94a3b8',
  negative:      '#ef4444',
};

function sentimentDotColor(sentiment: string | null): string {
  return sentiment ? (SENTIMENT_CAP8_COLOR[sentiment] ?? '#71717a') : '#71717a';
}

// ── Trend glyph ───────────────────────────────────────────────────────────
function TrendGlyph({ trend }: { trend: PulseClient['trend'] }) {
  const glyphs = { improving: '↑', stable: '→', degrading: '↓' } as const;
  return (
    <span className={`mood-chip-trend ${trend}`} aria-label={trend}>
      {glyphs[trend]}
    </span>
  );
}

// ── Mini sentiment dot strip (last 5 meetings, most-recent left) ──────────
function SentimentDots({ meetings }: { meetings: PulseClient['meetings'] }) {
  return (
    <span className="mood-chip-dots" aria-label="Recent meeting sentiments">
      {meetings.map((m) => (
        <span
          key={m.transcript_id}
          className="mood-chip-dot"
          style={{ backgroundColor: sentimentDotColor(m.sentiment_cap8) }}
          title={`${m.meeting_date}: ${m.sentiment_cap8 ?? 'unknown'}`}
        />
      ))}
    </span>
  );
}

// ── Ack button ────────────────────────────────────────────────────────────
interface AckButtonProps {
  client: PulseClient;
  onAck: (clientId: string) => void;
  saving: boolean;
}

function AckButton({ client, onAck, saving }: AckButtonProps) {
  if (client.acked) {
    const ackedAt = client.ack_info?.acked_at
      ? new Date(client.ack_info.acked_at).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        })
      : null;
    return (
      <span className="pulse-acked-label">
        Acknowledged{ackedAt ? ` ${ackedAt}` : ''}
      </span>
    );
  }
  return (
    <button
      className="btn btn-secondary btn-small"
      onClick={() => onAck(client.client_id)}
      disabled={saving}
    >
      {saving ? 'Saving…' : 'Acknowledge'}
    </button>
  );
}

// ── Single client row ─────────────────────────────────────────────────────
interface ClientRowProps {
  client: PulseClient;
  onAck: (clientId: string) => void;
  ackingSaving: boolean;
}

function ClientRow({ client, onAck, ackingSaving }: ClientRowProps) {
  const isDegrading = client.trend === 'degrading';
  const latestMeeting = client.meetings[0] ?? null;

  return (
    <div className={`pulse-client-row${isDegrading ? ' pulse-row-degrading' : ''}`}>
      {/* Compact summary line */}
      <div className="pulse-client-summary">
        <Link to={`/clients/${client.client_id}`} className="pulse-client-name">
          {client.client_name}
        </Link>
        <TrendGlyph trend={client.trend} />
        <SentimentDots meetings={client.meetings} />
        {client.open_commitments > 0 && (
          <span className="pulse-commitment-count" title="Open commitments">
            {client.open_commitments} open
          </span>
        )}
      </div>

      {/* Expanded section for degrading clients */}
      {isDegrading && (
        <div className="pulse-row-expanded">
          {client.degrading_reason && (
            <span className="pulse-degrading-reason">{client.degrading_reason}</span>
          )}
          {latestMeeting?.sentiment_cap8_reasoning && (
            <p className="pulse-reasoning-text">{latestMeeting.sentiment_cap8_reasoning}</p>
          )}
          <div className="pulse-ack-row">
            <AckButton
              client={client}
              onAck={onAck}
              saving={ackingSaving}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Commitment attention row ──────────────────────────────────────────────
function CommitmentRow({ commitment }: { commitment: Commitment }) {
  const isOverdue =
    commitment.due_date && new Date(commitment.due_date) < new Date();

  return (
    <div className="pulse-commitment-row">
      <span className="pulse-commitment-text" title={commitment.commitment_text}>
        {commitment.commitment_text.length > 80
          ? commitment.commitment_text.slice(0, 80) + '…'
          : commitment.commitment_text}
      </span>
      <Link to={`/clients/${commitment.client_id}`} className="pulse-commitment-client">
        {commitment.client_name ?? 'Client'}
      </Link>
      {commitment.due_date && (
        <span
          className={`pulse-commitment-due${isOverdue ? ' pulse-due-overdue' : ''}`}
        >
          {new Date(commitment.due_date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          })}
        </span>
      )}
      {commitment.owner && (
        <span className="pulse-commitment-owner">{commitment.owner}</span>
      )}
    </div>
  );
}

interface CommitmentSectionProps {
  title: string;
  items: Commitment[];
  pillClass: string;
}

function CommitmentSection({ title, items, pillClass }: CommitmentSectionProps) {
  if (items.length === 0) return null;
  return (
    <div className="pulse-commitment-section">
      <div className="pulse-commitment-section-header">
        <span className="pulse-commitment-section-title">{title}</span>
        <span className={`attention-pill ${pillClass}`}>{items.length}</span>
      </div>
      {items.map((c) => (
        <CommitmentRow key={c.commitment_id} commitment={c} />
      ))}
    </div>
  );
}

// ── Relationships panel ───────────────────────────────────────────────────
function RelationshipsPanel() {
  const { refreshBadges } = useOutletContext<AppOutletContext>();
  const [data, setData] = useState<PulseRelationshipsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ackingIds, setAckingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getPulseRelationships()
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError('Could not load relationship data.'); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  const handleAck = useCallback(
    async (clientId: string) => {
      if (!data) return;
      // Optimistic update
      setAckingIds((prev) => new Set(prev).add(clientId));
      const now = new Date().toISOString();
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          clients: prev.clients.map((c) =>
            c.client_id === clientId
              ? { ...c, acked: true, ack_info: { acked_by: null, acked_at: now } }
              : c
          ),
        };
      });
      try {
        await api.ackClientSentiment(clientId);
        refreshBadges();
      } catch {
        // Revert on error
        setData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            clients: prev.clients.map((c) =>
              c.client_id === clientId
                ? { ...c, acked: false, ack_info: null }
                : c
            ),
          };
        });
      } finally {
        setAckingIds((prev) => {
          const next = new Set(prev);
          next.delete(clientId);
          return next;
        });
      }
    },
    [data, refreshBadges]
  );

  if (loading) return <div className="pulse-loading">Loading…</div>;
  if (error) return <div className="pulse-error">{error}</div>;
  if (!data || data.clients.length === 0)
    return <EmptyState title="No clients yet" hint="Client relationship health will appear here." />;

  const { commitment_attention } = data;
  const hasAttention =
    commitment_attention.overdue_cap8.length > 0 ||
    commitment_attention.upcoming_cap8.length > 0 ||
    commitment_attention.waiting_on_clients.length > 0;

  return (
    <div className="pulse-panel card-glass">
      <h2 className="pulse-panel-question">Is anyone unhappy?</h2>

      <div className="pulse-client-list">
        {data.clients.map((client) => (
          <ClientRow
            key={client.client_id}
            client={client}
            onAck={handleAck}
            ackingSaving={ackingIds.has(client.client_id)}
          />
        ))}
      </div>

      {hasAttention && (
        <div className="pulse-attention-block">
          <h3 className="pulse-attention-heading">Commitment attention</h3>
          <CommitmentSection
            title="Overdue — ours"
            items={commitment_attention.overdue_cap8}
            pillClass="attention-pill-overdue"
          />
          <CommitmentSection
            title="Upcoming (14d) — ours"
            items={commitment_attention.upcoming_cap8}
            pillClass="attention-pill-upcoming"
          />
          <CommitmentSection
            title="Waiting on client"
            items={commitment_attention.waiting_on_clients}
            pillClass="attention-pill-stale"
          />
        </div>
      )}
    </div>
  );
}

// ── Market panel ──────────────────────────────────────────────────────────
function MarketPanel() {
  const [data, setData] = useState<PulseMarketData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getPulseMarket()
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError('Could not load market data.'); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="pulse-panel card-glass">
      <h2 className="pulse-panel-question">What&apos;s the weather?</h2>

      {loading && <div className="pulse-loading">Loading…</div>}
      {error && <div className="pulse-error">{error}</div>}
      {data && (
        <>
          <MoodTimeline
            meetings={data.meetings}
            articles={data.articles}
            moodPeriods={data.mood_periods}
          />
          <p className="pulse-market-note">
            When client mood and press mood diverge, ask why.
          </p>
        </>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────
export function Pulse() {
  return (
    <div className="page-content pulse-page">
      <RelationshipsPanel />
      <MarketPanel />
    </div>
  );
}
